import { type BreakRulesConfig, type HoursRateConfig, type OvertimeConfig, OvertimeConfigType, type OvertimeKind } from "../../../utils/models";
import type { TrainingPayStatus } from "./deepLink";
import type { DeepLinkRowMatcher } from "./useWageRuleDeepLink";

/*
 * Each Wage Settings section's answer to "which of my rows does this link mean?" — the per-section half
 * of `useWageRuleDeepLink`. Module-level so each is a stable effect dependency.
 */

const indicesWhere = <TRow>(rows: readonly TRow[], matches: (row: TRow) => boolean): number[] => rows.flatMap((row, index) => (matches(row) ? [index] : []));

type IdentifiedRow = { id?: string; name: string };

/**
 * DAT-906 · The exact rule a breakdown line named, by id — or, when that id is gone (every save issues
 * new ids until the next recalculation rewrites the breakdown), every row still carrying its name.
 * `[]` when the target names neither, so the caller can fall back further.
 */
export const indicesOfRule = <TRow extends IdentifiedRow>(rows: readonly TRow[], ruleId: string | undefined, rule: string | undefined): number[] => {
	if (ruleId !== undefined) {
		const byId = indicesWhere(rows, (row) => row.id === ruleId);
		if (byId.length > 0) return byId;
	}
	if (rule === undefined) return [];
	const name = rule.trim();
	return indicesWhere(rows, (row) => row.name.trim() === name);
};

/** The rule the breakdown line (or, for a legacy row, its label) named: by id, then by trimmed exact name. */
export const matchHoursRateRows: DeepLinkRowMatcher<HoursRateConfig> = (target, rows) => {
	if (target.section !== "hours-rate") return [];
	return indicesOfRule(rows, target.ruleId, target.rule);
};

/**
 * The rule types that can produce each overtime kind. An `all` rule is checked against both the daily
 * and the weekly limit (`calcaulte_cost_logic.py`), so it can have produced either; it is not checked
 * for consecutive days.
 */
export const OVERTIME_TYPES_BY_KIND: Readonly<Record<OvertimeKind, readonly OvertimeConfigType[]>> = {
	daily: [OvertimeConfigType.daily, OvertimeConfigType.all],
	weekly: [OvertimeConfigType.weekly, OvertimeConfigType.all],
	consecutive: [OvertimeConfigType.consecutive_day],
};

/**
 * The overtime rule a breakdown line named: by id — that rule even if its type was changed since, as its
 * hover card shows it changed — then by name among the rules of its kind; failing that, and for a legacy
 * label, which names only the kind, every rule of the kind. Several can share a kind across roles or
 * unions, so all candidates are marked rather than guessing one.
 */
export const matchOvertimeRows: DeepLinkRowMatcher<OvertimeConfig> = (target, rows) => {
	if (target.section !== "overtime") return [];
	const byId = target.ruleId === undefined ? [] : indicesWhere(rows, (row) => row.id === target.ruleId);
	if (byId.length > 0) return byId;
	const types = target.overtimeKind === undefined ? undefined : OVERTIME_TYPES_BY_KIND[target.overtimeKind];
	const ofKind = (index: number) => types === undefined || types.includes(rows[index].type);
	const named = indicesOfRule(rows, undefined, target.rule).filter(ofKind);
	if (named.length > 0 || types === undefined) return named;
	return indicesWhere(rows, (row) => types.includes(row.type));
};

/**
 * The break rule a breakdown line named, by id then name. A legacy break label names no rule, so there a
 * row is highlighted only when the department has exactly one — then it can only have been that one.
 */
export const matchBreakRulesRows: DeepLinkRowMatcher<BreakRulesConfig> = (target, rows) => {
	if (target.section !== "break-rules") return [];
	const named = indicesOfRule(rows, target.ruleId, target.rule);
	if (named.length > 0) return named;
	return rows.length === 1 ? [0] : [];
};

/** A Training & orientation card, identified by the status whose pay it sets. */
export type TrainingPayCard = { trainingStatus: TrainingPayStatus };

export const matchTrainingPayCards: DeepLinkRowMatcher<TrainingPayCard> = (target, cards) => {
	if (target.section !== "training-orientation" || target.trainingStatus === undefined) return [];
	const { trainingStatus } = target;
	return indicesWhere(cards, (card) => card.trainingStatus === trainingStatus);
};
